import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { APIError, useAxios } from '../core/_axios';
import { requestPasswordReset } from '../core/_requests';
import useLocalizedCopy from '../hooks/useLocalizedCopy';

// reCAPTCHA Enterprise site key (same as other auth pages)
const RECAPTCHA_SITE_KEY = '6LeKzhQsAAAAAGlinDX1qnJBCLzdmrw5b0lB1wyH';

// Declare grecaptcha type for Enterprise
declare global {
    interface Window {
        grecaptcha: {
            enterprise: {
                ready: (callback: () => void) => void;
                execute: (siteKey: string, options: { action: string }) => Promise<string>;
            };
        };
    }
}

/**
 * Get reCAPTCHA token - in production uses real reCAPTCHA, in development simulates
 * the async behavior to catch timing-related bugs locally.
 */
const getRecaptchaToken = async (action: string): Promise<string | null> => {
    if (process.env.NODE_ENV === 'production') {
        await new Promise<void>((resolve) => {
            window.grecaptcha.enterprise.ready(() => resolve());
        });

        return await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, {
            action
        });
    } else {
        // Development: simulate async behavior to catch timing bugs
        await new Promise<void>((resolve) => setTimeout(resolve, 50));
        return null;
    }
};

const ForgotPassword = () => {
    const { copy } = useLocalizedCopy();
    const axios = useAxios();

    const [emailAddress, setEmailAddress] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load reCAPTCHA Enterprise script
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') {
            const existingScript = document.querySelector(`script[src*="recaptcha/enterprise.js"]`);
            if (existingScript) return;

            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;
            script.async = true;
            document.head.appendChild(script);
        }
    }, []);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting) return;

        setSubmitting(true);
        setError(null);

        try {
            const recaptchaToken = await getRecaptchaToken('FORGOT_PASSWORD');
            await requestPasswordReset(axios, emailAddress, recaptchaToken);
            setSubmitted(true);
        } catch (exception) {
            console.error(exception);
            const errorMessage = exception instanceof APIError
                ? exception.message
                : 'An error occurred. Please try again.';
            setError(errorMessage);
            setSubmitting(false);
        }
    }, [axios, emailAddress, submitting]);

    // Success state - show confirmation message
    if (submitted) {
        return (
            <div className="flex items-center justify-center grow bg-center bg-no-repeat page-bg">
                <div className="card max-w-[420px] w-full">
                    <div className="card-body flex flex-col gap-5 p-10">
                        <div className="text-center">
                            <div className="flex items-center justify-center mb-5">
                                <img
                                    className="small-logo min-h-[22px] max-h-[50px] max-w-100"
                                    src="/design/web_logo_black_small.png"
                                    alt="Small Logo"
                                />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 leading-none mb-2.5">
                                {copy.Auth.forgotPassword?.checkEmail || 'Check Your Email'}
                            </h3>
                            <p className="text-gray-600 text-sm mb-5">
                                {copy.Auth.forgotPassword?.successMessage ||
                                    'If an account exists with this email, you will receive password reset instructions.'}
                            </p>
                            <Link className="btn btn-light" to="/auth/login">
                                {copy.Auth.forgotPassword?.backToLogin || 'Back to Login'}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center grow bg-center bg-no-repeat page-bg">
            <div className="card max-w-[420px] w-full">
                <form className="card-body flex flex-col gap-5 p-10" onSubmit={handleSubmit}>
                    <div className="text-center mb-2.5">
                        <div className="flex items-center justify-center mb-5">
                            <img
                                className="small-logo min-h-[22px] max-h-[50px] max-w-100"
                                src="/design/web_logo_black_small.png"
                                alt="Small Logo"
                            />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 leading-none mb-2.5">
                            {copy.Auth.forgotPassword?.title || 'Forgot Password'}
                        </h3>
                        <p className="text-gray-600 text-sm">
                            {copy.Auth.forgotPassword?.subtitle ||
                                'Enter your email address and we\'ll send you instructions to reset your password.'}
                        </p>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="form-label text-gray-900">
                            {copy.Auth.login.emailLabel}
                        </label>
                        <input
                            id="emailAddress"
                            name="emailAddress"
                            className="input"
                            placeholder={copy.Auth.login.emailPlaceholder}
                            type="email"
                            value={emailAddress}
                            onChange={(e) => setEmailAddress(e.target.value)}
                            required
                            autoFocus
                        />
                    </div>

                    <button
                        className="btn btn-primary flex justify-center grow"
                        type="submit"
                        disabled={submitting}
                    >
                        {!submitting
                            ? (copy.Auth.forgotPassword?.sendButton || 'Send Reset Link')
                            : (copy.Auth.forgotPassword?.sending || 'Sending...')
                        }
                    </button>

                    {error && (
                        <div className="text-center p-4 text-sm text-red-800 rounded-lg bg-red-50" role="alert">
                            {error}
                        </div>
                    )}

                    <div className="text-center">
                        <Link className="text-2sm link" to="/auth/login">
                            {copy.Auth.forgotPassword?.backToLogin || 'Back to Login'}
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ForgotPassword;
